import { useState, useEffect } from "react"
import { useGlobalContext, encryptUsername } from "../data/GlobalContext"
import { encrypt } from "../data/CryptoUtil"
import { formatDate } from "./request_history/TransactionHistory"
import { apiJSONURL } from "../../App"
import UsernameMsgBox from "./UsernameMsgBox"

export default function Account({ onClose }) {
  const [windowWidth, setWindowWidth] = useState(window.innerWidth)
  const { username, playerDetails, fetchPlayerDetails } = useGlobalContext()

  const [isEditingUsername, setIsEditingUsername] = useState(false)
  const [isEditingEmail, setIsEditingEmail] = useState(false)
  const [displayEmail, setDisplayEmail] = useState(false)
  const [displayUsername, setDisplayUsername] = useState("")
  const [error, setError] = useState("")
  const [msgBox, setMsgBox] = useState(false)
  const [success, setSuccess] = useState("")
  const [isSaving, setIsSaving] = useState(false)
  const [cooldown, setCooldown] = useState(false)

  const isLoading = !playerDetails

  // Handle responsive design
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth)
    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [])

  // Initialize displayUsername directly from context
  useEffect(() => {
    if (playerDetails?.customUsername) {
      setDisplayUsername(playerDetails.customUsername)
    } else if (username) {
      setDisplayUsername(username)
    }
  }, [username, playerDetails])

  useEffect(() => {
    setDisplayEmail(playerDetails.email || "")
  }, [playerDetails.email])

  const handleUsernameChange = (e) => {
    setDisplayUsername(e.target.value)
  }

  // Edit username
  const handleSaveUsername = async () => {
    if (isSaving || cooldown) return

    // Check for empty field
    if (!displayUsername.trim()) {
      setError("Username cannot be empty.")
      setTimeout(() => setError(""), 2000)
      return
    }

    // Limit characters for username
    if (displayUsername.length < 6 || displayUsername.length > 15) {
      setError("Username must be between 6 and 15 characters.")
      setTimeout(() => setError(""), 2000)
      return
    }

    const usernamePattern = /^(?=.*[a-zA-Z])[a-zA-Z0-9_]+$/
    const repeatingChars = /(.)\1{3,}/ // 4+ same char
    const gibberishList = ["asdasd", "qwerty", "zxcv", "test", "abc123"]

    if (!usernamePattern.test(displayUsername.toLowerCase())) {
      setError(
        "Username must contain only letters, numbers, or underscores, and at least one letter."
      )
      setTimeout(() => setError(""), 2000)
      return
    }

    if (repeatingChars.test(displayUsername)) {
      setError("Username cannot contain excessive repeating characters.")
      setTimeout(() => setError(""), 2000)
      return
    }

    if (
      gibberishList.some((word) => displayUsername.toLowerCase().includes(word))
    ) {
      setError("Username looks invalid, please choose another.")
      setTimeout(() => setError(""), 2000)
      return
    }

    setError("")
    setIsSaving(true)

    try {
      const normalizedUsername = displayUsername.toLowerCase()
      const params = new URLSearchParams({
        cmd: "createUsername",
        u: username,
        customUsername: normalizedUsername,
      })
      const res = await fetch(`${apiJSONURL}?${params}`)
      const raw = await res.text()

      let data = JSON.parse(raw)

      if (data === "s" || data?.s) {
        setIsEditingUsername(false)
        setMsgBox(true)
        // setSuccess(
        //   data === "s"
        //     ? "Successfully changed"
        //     : data.s || "Successfully changed"
        // )

        // Auto close message box after 5 seconds
        setTimeout(() => {
          setMsgBox(false)
        }, 5000)

        // Refresh player details after 5 seconds to show updated username
        setTimeout(() => {
          fetchPlayerDetails(username)
        }, 1000)
      } else if (data === "e" || data?.e) {
        setIsEditingUsername(true)
        setMsgBox(false)
        setError(
          typeof data === "string" ? "Username already created." : data.e
        )
        setTimeout(() => setError(""), 2000)
      } else {
        setError("Something went wrong. Please try again.")
        setTimeout(() => setError(""), 2000)
      }
    } finally {
      setIsSaving(false)
      setCooldown(true)
      setTimeout(() => setCooldown(false), 2000)
    }
  }

  // Edit email address
  const handleSaveEmail = async () => {
    if (isSaving || cooldown) return

    const email = displayEmail?.trim()

    if (!email) {
      setError("Email cannot be empty.")
      setTimeout(() => setError(""), 2000)
      return
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(email)) {
      setError("Please enter a valid email address.")
      setTimeout(() => setError(""), 2000)
      return
    }

    setError("")
    setIsSaving(true)

    try {
      const normalizedEmail = email.toLowerCase()

      const encryptedUsername = await encrypt(username)

      const params = new URLSearchParams({
        cmd: "updateEmail",
        u: encryptedUsername,
        email: normalizedEmail,
      })

      const res = await fetch(`${apiJSONURL}?${params}`)
      const data = await res.json()

      if (data?.isUpdateSuccess) {
        setIsEditingEmail(false)
        setMsgBox(true)

        setTimeout(() => setMsgBox(false), 5000)
        setTimeout(() => fetchPlayerDetails(username), 1000)
      } else {
        setIsEditingEmail(true)
        setMsgBox(false)
        setError("Email already used.")
        setTimeout(() => setError(""), 2000)
      }
    } finally {
      setIsSaving(false)
      setCooldown(true)
      setTimeout(() => setCooldown(false), 2000)
    }
  }
  if (isLoading) {
    return (
      <div className="openProfileItems-container">
        <div className="openAccount">
          <div className="openProfile-Label">Loading Account Info...</div>
        </div>
      </div>
    )
  }

  return (
    <div className="openProfileItems-container">
      {windowWidth <= 840 && (
        <div className="close-profile-module-container">
          <div className="profile-logo">
            <a href="/">
              <img src="/images/icon/fgh-logo.webp" alt="" />
            </a>
          </div>
          <button className="close-profile-module-btn" onClick={onClose}>
            <img src="/images/icon/ARROW.webp" alt="" /> Back
          </button>
        </div>
      )}
      <div className="openAccount">
        <div className="openProfile-Label">Overview</div>

        {/* Name */}
        <div className="openProfile-input">
          <strong>Name:</strong>
          <span>{playerDetails.playername || "Not Set"}</span>
        </div>

        {/* Username - Editable */}
        <div className="openProfile-input">
          {isEditingUsername ? (
            <>
              <div className="profile-row profile-row__content">
                <div className="profile-row__edit-row">
                  <div className="profile-row__label">
                    <strong>Username:</strong>
                  </div>
                  <div>
                    <input
                      type="text"
                      onChange={handleUsernameChange}
                      minLength={6}
                      maxLength={15}
                      placeholder="Please enter username"
                      className={`${error && "input-border"}`}
                    />
                    {error && <div className="error-text">{error}</div>}
                  </div>
                </div>
                <div className="profile-row__buttons">
                  <button onClick={handleSaveUsername}>Save</button>
                  <button
                    onClick={() => {
                      setDisplayUsername(username)
                      setIsEditingUsername(false)
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div className="profile-row">
              <strong>Username:</strong>
              <span className="username-text">
                {playerDetails.customUsername || "Not Set"}
              </span>
              <img
                className="fgh-edit"
                src="/images/icon/username-edit.webp"
                alt="Edit"
                onClick={() => setIsEditingUsername(true)}
              />
            </div>
          )}
          {/* <div className="success-text">Successfully Change</div> */}
          {/* {success && <div className="success-text">{success}</div>} */}
        </div>
        {msgBox && <UsernameMsgBox />}

        <div className="openProfile-input">
          {isEditingEmail ? (
            <div className="profile-row profile-row__content">
              <div className="profile-row__edit-row">
                <div className="profile-row__label">
                  <strong>Email:</strong>
                </div>
                <div>
                  <input
                    type="email"
                    onChange={(e) => setDisplayEmail(e.target.value)}
                    placeholder="Please enter email"
                    className={`${error && "input-border"}`}
                  />
                  {error && <div className="error-text">{error}</div>}
                </div>
              </div>
              <div className="profile-row__buttons">
                <button onClick={handleSaveEmail}>Save</button>
                <button
                  onClick={() => {
                    setIsEditingEmail(false)
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="profile-row">
              <strong>Email:</strong>
              <span className="email-text">
                {playerDetails.email || "Not Set"}
              </span>
              <img
                className="fgh-edit"
                src="/images/icon/username-edit.webp"
                alt="Edit"
                onClick={() => setIsEditingEmail(true)}
              />
            </div>
          )}
        </div>

        {/* Other Fields */}
        <div className="openProfile-input">
          <strong>Phone:</strong>
          <span>{playerDetails.mobile || "Not Set"}</span>
        </div>

        <div className="openProfile-input">
          <strong>Status:</strong>
          <span>{playerDetails.status || "Not Set"}</span>
        </div>

        <div className="openProfile-input">
          <strong>Start Date:</strong>
          <span>{formatDate(playerDetails.startdate) || "Not Set"}</span>
        </div>

        <div className="openProfile-input">
          <strong>Referrer:</strong>
          <span>
            {playerDetails.referral && playerDetails.referral !== "0"
              ? playerDetails.referral
              : "Not Set"}
          </span>
        </div>
      </div>
    </div>
  )
}
